import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import useAxios from '../../../../../../hooks/useAxios';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import { PriceListCollection } from '../../../../../../types/api/priceLists';
import { debounceTimeout } from '../../../../../../utils/constants/misc';
import { bankPrefixes } from '../../../../../../utils/globals';
import { queryString } from '../../../../../../utils/helpers/http';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import {
  ChargingDepartments,
  Headquarters,
  isHeadquarter,
} from '../CreateEditDialog.functions';
import BankAccountField from './Fields/BankAccountField';
import CodBankAccountPaymentField from './Fields/CodBankAccountPaymentField';
import PersonalSubjectNoField from './Fields/PersonalSubjectNoField';
import PriceListField from './Fields/PriceListField';
import PriceListFieldRecipient from './Fields/PriceListFieldRecipient';
import WorkingHoursField from './Fields/WorkingHoursField';

type Props = {
  clientData: any;
  invoicingCheckboxProperties: any;
  fieldSettings: any;
  isEditDialog: boolean;
};

function getBankName(value: string) {
  const bank = Object.keys(bankPrefixes).find(
    (k) => k === value.slice(0, 3)
  ) as keyof typeof bankPrefixes | undefined;

  return bank ? bankPrefixes[bank] : null;
}

function LegalDataTab({
  clientData,
  invoicingCheckboxProperties,
  fieldSettings,
  isEditDialog,
}: Props) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const direkcija_ime = useWatch({ name: 'direkcija_ime' });
  const direkcija_id = useWatch({ name: 'direkcija_id' });
  const pravno_lice = useWatch({ name: 'pravno_lice' });
  const faktura = useWatch({ name: 'faktura' });
  const danocen_broj = useWatch({ name: 'danocen_broj' });
  const departments = useWatch({ name: 'departments' });

  const isElsPhysicalPerson =
    process.env.REACT_APP_CLIENT_NAME === 'ecologistic' && !pravno_lice;

  useEffect(() => {
    if (isElsPhysicalPerson && faktura) {
      setValue('faktura', false);
    }
  }, [isElsPhysicalPerson, faktura, setValue]);

  const { data: chargingDepartmentsData } = useAxiosHook<ChargingDepartments[]>(
    `/clients/${direkcija_id}/departments`,
    {
      skipWhen: !pravno_lice || !direkcija_id || direkcija_id === 0,
    }
  );

  const { data: priceLists, isLoading: isPriceListsLoading } =
    useAxiosHook<PriceListCollection>('/price-lists?limit=0');

  const priceListOptions = useMemo(
    () =>
      priceLists && priceLists?.data.length > 0
        ? priceLists?.data.map((p) => ({
            label: p.name ?? '',
            value: p.id,
          }))
        : [],
    [priceLists]
  );

  const invoicingAndCODDepartmentOptions = useMemo(() => {
    if (chargingDepartmentsData && chargingDepartmentsData.length > 0) {
      return chargingDepartmentsData.map((department: any) => ({
        label: department.name,
        value: department.id,
      }));
    }

    if (direkcija_id === 0 || direkcija_id === clientData?.id) {
      return departments?.map((department: any) => ({
        label: department.name,
        value: department.id,
      }));
    }

    return [];
  }, [chargingDepartmentsData, departments, direkcija_id, clientData?.id]);

  const invoiceDateBasisTypeIdOptions = useMemo(
    () => [
      { label: t('Date of receipt'), value: 1 },
      { label: t('Date of delivery'), value: 2 },
    ],
    [t]
  );

  const debouncedImeClientsHQ = useDebounce(direkcija_ime, debounceTimeout);

  const {
    data: headquarterData,
    reload: reloadData,
    isLoading: isLoadingData,
  } = useAxios();

  const onChangeHeadquarterId = useCallback(
    (e) => {
      setValue('direkcija_id', e);

      setValue(
        'departments',
        isEditDialog && clientData?.departments?.length > 0
          ? clientData.departments
          : [
              {
                id: -1,
                name: 'Default',
                headquarters_id: clientData.id ?? 0,
                charge_customer_id: 0,
              },
            ]
      );

      if (e === null || parseInt(clientData?.id) === e) {
        setValue('cenovnik_id', clientData.cenovnik_id ?? '');
        setValue('maticen_broj', clientData.maticen_broj ?? '');
        setValue('danocen_broj', clientData.danocen_broj ?? '');
        return;
      }

      reloadData({
        url: `/clients/${e}`,
        successCallback: (client: any) => {
          setValue('cenovnik_id', client.cenovnik_id ?? '');
          setValue('maticen_broj', client.maticen_broj ?? '');
          setValue('danocen_broj', client.danocen_broj ?? '');
        },
      });
    },
    [clientData, reloadData, isEditDialog, setValue]
  );

  const { data: headquarters, isLoading: isHeadquartersLoading } = useAxiosHook<
    WithData<Headquarters[]>
  >(
    {
      url:
        '/clients/hq/ref' +
        queryString({ ime: debouncedImeClientsHQ, page: 1, limit: 0 }),
    },
    { skipWhen: debouncedImeClientsHQ === t('This client is a headquarter') }
  );

  const headQuarterOptions = useMemo(() => {
    const clientId =
      isEditDialog && clientData?.id ? parseInt(clientData.id) : 0;

    const defaultOption = [
      { label: t('This client is a headquarter'), value: clientId },
    ];

    const options =
      headquarters?.data
        ?.filter((hq) => hq.id !== clientId) // Exclude current client ID in edit mode
        .map((hq) => ({
          label: hq.ime,
          value: hq.id,
        })) || [];

    return [...defaultOption, ...options];
  }, [headquarters, clientData?.id, isEditDialog, t]);

  return (
    <div className="legal-data-tab p-fluid">
      <div className="p-grid">
        <div className="p-col-12 p-md-6">
          {pravno_lice && (
            <FieldWithErrors name="broj_dogovor" label={t('Contract No.')}>
              <Controller
                name="broj_dogovor"
                render={({ field }) => (
                  <InputText
                    id="broj_dogovor"
                    name="broj_dogovor"
                    value={field.value}
                    onChange={field.onChange}
                    max={100}
                  />
                )}
              />
            </FieldWithErrors>
          )}
          <PersonalSubjectNoField
            label={t('Social Security No.')}
            maxLength={7}
            disabled={isLoadingData}
          />
          {pravno_lice && (
            <FieldWithErrors name="danocen_broj" label={t('Tax No.')}>
              <Controller
                name="danocen_broj"
                render={({ field }) => (
                  <InputText
                    tooltip={
                      !danocen_broj?.length || danocen_broj.length < 3
                        ? ''
                        : String(getBankName(danocen_broj ?? '') ?? '')
                    }
                    id="danocen_broj"
                    name="danocen_broj"
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/[^MKМК\d]/g, ''))
                    }
                    max={15}
                    disabled={isLoadingData}
                  />
                )}
              />
            </FieldWithErrors>
          )}
          <BankAccountField />
        </div>

        <div className="p-col-12 p-md-6">
          {pravno_lice && (
            <>
              <FieldWithErrors name="direkcija_id" label={t('Headquarter')}>
                <Controller
                  name="direkcija_id"
                  render={({ field }) => (
                    <AutoCompleteInput
                      placeholder={t('Search by name')}
                      options={headQuarterOptions}
                      value={field.value}
                      filterValue={direkcija_ime ?? ''}
                      onFilterChange={(e) => setValue('direkcija_ime', e)}
                      onSelectionChange={onChangeHeadquarterId}
                      isLoading={isHeadquartersLoading}
                      filterDataCy="direkcija_id"
                      optionsClassName="data-cy-headquarter"
                    />
                  )}
                />
              </FieldWithErrors>

              <FieldWithErrors name="rok_plakanje" label={t('Payment due')}>
                <Tooltip
                  target=".payment-due-tooltip"
                  position="left"
                  style={
                    invoicingCheckboxProperties?.checked
                      ? { display: 'none' }
                      : {}
                  }
                />

                <div
                  className="payment-due-tooltip"
                  data-pr-tooltip={t('Invoicing is required')}
                >
                  <Controller
                    name="rok_plakanje"
                    render={({ field }) => (
                      <InputText
                        id="rok_plakanje"
                        name="rok_plakanje"
                        value={field.value}
                        disabled={!invoicingCheckboxProperties.checked}
                        onChange={(e) =>
                          field.onChange(e.target.value.replace(/\D/g, ''))
                        }
                        data-cy="payment_due"
                      />
                    )}
                  />
                </div>
              </FieldWithErrors>

              <WorkingHoursField />
            </>
          )}

          {isHeadquarter(pravno_lice, clientData, direkcija_id) && (
            <FieldWithErrors
              name="same_price_list_for_subsidiaries"
              label={false}
              className="p-d-flex p-mb-2 p-jc-between"
            >
              <Controller
                name="same_price_list_for_subsidiaries"
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="same_price_list_for_subsidiaries"
                      name="same_price_list_for_subsidiaries"
                      checked={field.value}
                      value={field.value}
                      onChange={field.onChange}
                      className="data-cy-same_price_list_for_subsidiaries"
                    />
                    <label
                      htmlFor="same_price_list_for_subsidiaries"
                      data-cy="same_price_list_for_subsidiaries"
                    >
                      {t('Only price list for the subsidiaries of this HQ')}
                    </label>
                  </>
                )}
              />
            </FieldWithErrors>
          )}

          <PriceListField
            priceListOptions={priceListOptions}
            isHeadquarter={isHeadquarter}
            headquarterData={headquarterData}
            isHeadquarterDataLoading={isLoadingData}
            isPriceListsLoading={isPriceListsLoading}
          />

          <PriceListFieldRecipient
            priceListOptions={priceListOptions}
            isPriceListsLoading={isPriceListsLoading}
            isHeadquarter={isHeadquarter}
            headquarterData={headquarterData}
            isHeadquarterDataLoading={isLoadingData}
          />
        </div>
      </div>

      <span className="dialog-section-divider">{t('Settings')}</span>

      <div className="p-grid">
        <div className="p-col-12 p-md-6">
          {(pravno_lice || isElsPhysicalPerson) && (
            <FieldWithErrors
              name="faktura"
              label={false}
              className="p-d-flex p-mb-2 p-jc-between"
            >
              <Controller
                name="faktura"
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="faktura"
                      name="faktura"
                      checked={field.value}
                      value={field.value}
                      onChange={field.onChange}
                      {...invoicingCheckboxProperties}
                      {...(isElsPhysicalPerson
                        ? { checked: false, disabled: true }
                        : {})}
                      className="data-cy-invoicing"
                    />
                    <label htmlFor="faktura" data-cy="faktura">
                      {t('Invoicing')}
                    </label>
                  </>
                )}
              />
            </FieldWithErrors>
          )}
          <CodBankAccountPaymentField />

          <FieldWithErrors
            name="cod_email_warrant"
            label={false}
            className="p-d-flex p-mb-2 p-jc-between"
          >
            <Controller
              name="cod_email_warrant"
              render={({ field }) => (
                <>
                  <Checkbox
                    id="cod_email_warrant"
                    name="cod_email_warrant"
                    checked={field.value}
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <label
                    htmlFor="cod_email_warrant"
                    data-cy="cod_email_warrant"
                  >
                    {t('Automated sending of COD warrant')}
                  </label>
                </>
              )}
            />
          </FieldWithErrors>
          {pravno_lice &&
            faktura &&
            fieldSettings.ConfigurableInvoiceSendingEmail && (
              <FieldWithErrors
                name="invoice_email_warrant"
                label={false}
                className="p-d-flex p-mb-2"
              >
                <Controller
                  name="invoice_email_warrant"
                  render={({ field }) => (
                    <>
                      <Checkbox
                        id="invoice_email_warrant"
                        name="invoice_email_warrant"
                        checked={field.value}
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <label
                        htmlFor="invoice_email_warrant"
                        data-cy="invoice_email_warrant"
                      >
                        {t('Automated sending invoice copy on email')}
                      </label>
                    </>
                  )}
                />
              </FieldWithErrors>
            )}

          {faktura && (
            <FieldWithErrors
              name="invoice_date_basis_type_id"
              label={t('Billing Date Type')}
            >
              <Controller
                name="invoice_date_basis_type_id"
                render={({ field }) => (
                  <Dropdown
                    id="invoice_date_basis_type_id"
                    name="invoice_date_basis_type_id"
                    options={invoiceDateBasisTypeIdOptions}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWithErrors>
          )}
        </div>

        {/* {pravno_lice ? ( */}
        <div className="p-col-12 p-md-6">
          <FieldWithErrors name="cod_department_id" label={t('COD Department')}>
            <Controller
              name="cod_department_id"
              render={({ field }) => (
                <Dropdown
                  id="cod_department_id"
                  name="cod_department_id"
                  options={invoicingAndCODDepartmentOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="invoice_department_id"
            label={t('Invoicing Department')}
          >
            <Controller
              name="invoice_department_id"
              render={({ field }) => (
                <Dropdown
                  id="invoice_department_id"
                  name="invoice_department_id"
                  options={invoicingAndCODDepartmentOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>
        </div>
        {/* ) : (
          <></>
        )} */}
      </div>
    </div>
  );
}

export default LegalDataTab;
